import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, Target, Heart, Users, TrendingUp, Shield } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Award,
      title: "Expertise",
      description: "Une connaissance approfondie du marché de l'assurance IARD et des besoins spécifiques de nos clients."
    },
    {
      icon: Heart,
      title: "Écoute",
      description: "Une attention particulière portée à chaque client pour des solutions vraiment personnalisées."
    },
    {
      icon: Shield,
      title: "Fiabilité",
      description: "Un engagement sans faille pour protéger vos intérêts et vous accompagner sur le long terme."
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "Des services modernes comme la souscription 24/7 pour répondre à vos besoins d'aujourd'hui."
    }
  ];

  return (
    <>
      <Helmet>
        <title>À Propos | CAMBC Conseils - Courtier Assurance Moissac</title>
        <meta name="description" content="Cabinet CAMBC Conseils, courtier assurance à Moissac. Alexandre MEUX et son équipe vous accompagnent avec expertise depuis 2024." />
        <meta name="keywords" content="à propos CAMBC Conseils, courtier assurance Moissac, Alexandre MEUX, cabinet assurance 82200" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/about" />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">À Propos</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cabinet CAMBC Conseils, votre partenaire de confiance en courtage d'assurance à Moissac
          </p>
        </div>

        {/* About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <img
              src="https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBpbnN1cmFuY2UlMjBhZHZpc29yfGVufDB8fHx8MTc3MTc5NTg3Mnww&ixlib=rb-4.1.0&q=85"
              alt="Cabinet CAMBC Conseils"
              className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
            />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Notre Engagement</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Chez <span className="font-semibold text-blue-700">Cabinet CAMBC Conseils</span>, nous sommes spécialisés dans le courtage d'assurance IARD (Incendie, Accidents et Risques Divers) pour les particuliers et les professionnels.
              </p>
              <p>
                Implantés au cœur de Moissac, nous mettons notre expertise et notre proximité au service de nos clients pour leur offrir des solutions d'assurance adaptées à leurs besoins spécifiques.
              </p>
              <p>
                Notre objectif est simple : <span className="font-semibold">vous protéger efficacement</span> tout en vous accompagnant avec transparence et professionnalisme dans toutes vos démarches d'assurance.
              </p>
              <p>
                Avec notre service de souscription disponible <span className="font-semibold text-blue-700">24h/24 et 7j/7</span>, nous garantissons une disponibilité maximale pour répondre à vos besoins, même en dehors des heures ouvrables.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Valeurs</h2>
            <p className="text-xl text-gray-600">
              Ce qui nous guide au quotidien pour vous servir au mieux
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <Card key={idx} className="text-center hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-blue-700" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pourquoi Nous Choisir ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                  <Users className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Conseil Personnalisé</h3>
                <p className="text-gray-700">
                  Chaque client est unique. Nous prenons le temps d'analyser votre situation pour vous proposer des solutions vraiment adaptées à vos besoins.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                  <Target className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expertise IARD</h3>
                <p className="text-gray-700">
                  Une maîtrise complète des assurances de biens et de responsabilité pour vous offrir la meilleure protection possible.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                  <Shield className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Service Complet</h3>
                <p className="text-gray-700">
                  De l'assurance classique aux démarches de carte grise, nous gérons tous vos besoins administratifs en un seul endroit.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Disponibilité 24/7</h3>
                <p className="text-gray-700">
                  Notre service de souscription en ligne est accessible à toute heure pour répondre à vos besoins urgents d'assurance temporaire.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-blue-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Faisons Connaissance
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Venez nous rencontrer à notre cabinet de Moissac ou contactez-nous pour échanger sur vos projets d'assurance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 px-8">
                Prendre rendez-vous
              </Button>
            </Link>
            <a href="tel:0582819120">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8">
                05 82 81 91 20
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
